import { motion } from 'framer-motion';
import { Star, Quote, Play } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";

export default function Testimonials() {
  const testimonials = [
    {
      name: 'Amit P Thorvatt',
      role: 'CEO, Acore Works (Furniture Manufacture)',
      rating: 5,
      text: 'Recently, I had the chance to visit Dr. Chandrashekar Harale from Vijaya dental clinic for my dental checkup and treatment. The clinic provides a comfortable, clean, and contemporary setting, and Dr. Chandrashekar Harale\'s skill and dedication are exceptionally impressive. Whether it was a check-up, cleaning, or more intricate procedures, all were administered with precise care and professionalism. The team was polite, on time, and made sure the whole experience was easy and stress-free. I would strongly recommend Dr. Chandrashekar Harale at Vijaya Dental Clinic to anyone seeking trustworthy, patient, and high-quality dental treatment!',
      image: null
    },
    {
      name: 'Priya Patil',
      role: 'Working Professional',
      rating: 5,
      text: 'Dr. Harale is an exceptional dentist! His expertise in BPS dentures gave my father his confidence back. The clinic is spotlessly clean and the staff is very caring. Highly recommend for senior citizens needing denture work.',
      image: null
    },
    {
      name: 'Rajesh Kulkarni',
      role: 'Business Owner',
      rating: 5,
      text: 'I was extremely nervous about my root canal treatment, but Dr. Harale made it completely painless. His gentle approach and modern techniques really helped ease my anxiety. The clinic environment is very professional and comfortable.',
      image: null
    },
    {
      name: 'Sneha Deshmukh',
      role: 'Teacher',
      rating: 5,
      text: 'Amazing experience with smile design! Dr. Harale transformed my smile with dental laminates. The results are natural-looking and beautiful. The consultation was thorough and he explained every step of the process.',
      image: null
    },
    {
      name: 'Anil Kumbhar',
      role: 'Retired Senior Citizen',
      rating: 5,
      text: 'As a senior citizen, I appreciate the patience and care Dr. Harale shows. My dental implant procedure was smooth and the follow-up care was excellent. The location near the railway station is also very convenient.',
      image: null
    },
    {
      name: 'Meera Joshi',
      role: 'Homemaker',
      rating: 5,
      text: 'Our family has been visiting Vijaya Dental Clinic for years. Dr. Harale treats everyone from my children to my elderly in-laws with the same level of care and expertise. Truly a family dentist we can trust.',
      image: null
    }
  ];

  return (
    <div data-testid="testimonials-page">
      <section className="pt-32 pb-20 bg-gradient-to-r from-blue-50 to-cyan-50" data-testid="testimonials-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-sm font-bold text-primary uppercase tracking-widest">Testimonials</span>
            <h1 className="font-heading font-bold text-5xl sm:text-6xl text-slate-900 mt-4 mb-6">
              What Our Patients Say
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Real experiences from real patients who trust us with their dental care
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="testimonials-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                viewport={{ once: true }}
                className="relative bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-xl transition-all duration-300"
                data-testid={`testimonial-${index}`}
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center shadow-lg">
                  <Quote className="w-6 h-6 text-white" />
                </div>

                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                <p className="text-slate-700 leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-600">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="video-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-4xl text-slate-900 mb-4">
              Video Testimonials
            </h2>
            <p className="text-xl text-slate-600">
              Watch our patients share their experiences
            </p>
          </motion.div>

          <Carousel className="max-w-5xl mx-auto">
            <CarouselContent>
              <CarouselItem>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-slate-900">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                    title="Patient Video Testimonial 1"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-slate-900">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                    title="Patient Video Testimonial 2"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-slate-900">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                    title="Patient Video Testimonial 3"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>

          <div className="text-center mt-12">
            <p className="text-slate-600 text-lg">
              Want to share your experience? <a href="tel:09975727098" className="text-primary font-semibold hover:underline">Contact us</a>
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="rating-summary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-block mb-8">
              <div className="flex items-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-5xl font-heading font-bold text-slate-900 mb-2">5.0</p>
              <p className="text-slate-600">Based on 5000+ patient reviews</p>
            </div>

            <h2 className="font-heading font-bold text-4xl text-slate-900 mb-6">
              Join Our Happy Patients
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Experience the care that has earned us thousands of five-star reviews
            </p>
            
            <a
              href="tel:09975727098"
              data-testid="book-from-testimonials-button"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              Book Your Appointment
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
